/**
 * Which messages belong on the food list right now?
 *
 * The food search page can show two things that are not food cards: a CARD ABOVE the list, and
 * a MESSAGE BELOW it. At most one of each, never two of either.
 *
 * The message below closes the list. Some of them replace an empty list ("use the search bar
 * above", "no food found for that barcode", "couldn't load foods"), and some sit under a list
 * that does have results ("not the right food?").
 *
 * The card above the list appears only when the server-side food search did not contribute to
 * what is on screen, so the list holds nothing but foods already stored on this device (#5114).
 * It is above the results on purpose: the user who reported that ticket read a whole screen of
 * unrelated foods without ever learning that the search had failed, and a note under the list
 * would have been below the fold.
 *
 * That rule used to be maintained by hand. Each message had its own `@if` in foods.html
 * repeating every sibling's condition and negating it, which meant adding a message meant
 * editing all the others — #3743 and #4100 each paid that toll — and the two typed-search
 * conditions had grown to six conjuncts. Missing one negation would render two messages at
 * once, which no build, lint or test could catch, so the whole decision moved here.
 *
 * This module holds no Angular, no DOM and no injected service, so a plain-Node unit test can
 * ask it every question. Everything about how a message LOOKS — its icon, its text, its
 * buttons — stays in the template; this only names which one to show.
 */

/** Every message the bottom of the food list can show, plus "show nothing". */
export enum FoodsListMessage {
  /** Nothing to say: a search is running, or results are on screen and there is no note for them. */
  None = 'none',
  /** The search box is empty and there is nothing to list yet. */
  UseSearchBar = 'use-search-bar',
  /** The search pass failed outright, so there is no list at all (#4100). */
  SearchFailed = 'search-failed',
  /** A barcode was searched for and nothing matched it. */
  NoResultsForBarcode = 'no-results-for-barcode',
  /** Text was searched for and nothing matched it. */
  NoResultsForText = 'no-results-for-text',
  /** The search box is empty, a filter is on, and nothing is listed: the filter may be why. */
  NothingMatchesFilters = 'nothing-matches-filters',
  /** Text was searched for with a filter on, and nothing matched: the filter may be why. */
  NoResultsForTextWithFilters = 'no-results-for-text-with-filters',
  /** Nothing matched, but only because the server search did not answer (#3743, #5114). */
  NoResultsConnectionError = 'no-results-connection-error',
  /** Barcode results are showing and the label scanner offers a better answer. */
  ResultsOfferScanLabel = 'results-offer-scan-label',
  /** Results are showing and none of them may be what the user wanted. */
  ResultsNotWhatYouWanted = 'results-not-what-you-wanted',
}

/**
 * How the server-side food search ended for the search currently on screen.
 *
 * This replaced a plain `searchConnectionError` boolean (#5114). The boolean could only say
 * "the server could not be reached", and it was set for exactly one class of failure — the
 * ones classified as transient. A request that TIMED OUT is deliberately not in that class, so
 * it set nothing, and the page went on to show the user's locally stored foods with no hint
 * that anything had gone wrong. Naming the outcome instead of flagging one case of it means a
 * failure can no longer fall between the branches.
 */
export enum ServerSearchOutcome {
  /**
   * The server search contributed whatever it had to give. This is also the answer when the
   * server was never asked — text shorter than three characters is not sent, and a logged-out
   * session returns nothing without failing. Neither is a failure, so neither shows a card.
   */
  Contributed = 'contributed',
  /** The request never reached the server: a connectivity failure classified as transient (#3743). */
  Unreachable = 'unreachable',
  /**
   * The request failed for any other reason — a timeout, a non-2xx answer, an unreadable body.
   * The timeout that caused #5114 lands here. These are still reported to the error tracker
   * exactly as they were before; this outcome only decides what the user is told.
   */
  Failed = 'failed',
}

/** The card shown ABOVE the food list, plus "show nothing". */
export enum FoodsSearchCard {
  /** The server search contributed, so the list needs no explaining. */
  None = 'none',
  /** The device looks offline. Gentler wording, and an info card rather than a warning one. */
  OfflineShowingLocalFoods = 'offline-showing-local-foods',
  /** The device believes it is online, so the food database itself is the problem. */
  ServerProblemShowingLocalFoods = 'server-problem-showing-local-foods',
  /**
   * The same two cases, for a BARCODE search, which needs its own wording for one reason: the
   * staples are not offered on a barcode search at all. `withOfflineStaples` returns the list
   * untouched when the search was a barcode (`staple-offline-search.ts`), because a barcode names
   * one exact product and a list of plain foods answers a different question. So the cards above
   * must not say staple foods are being shown — on this path they never are.
   */
  OfflineShowingLocalFoodsBarcode = 'offline-showing-local-foods-barcode',
  /** As above, for a device that believes it is online. */
  ServerProblemShowingLocalFoodsBarcode = 'server-problem-showing-local-foods-barcode',
}

/** Everything the choice depends on. All of it is already on the page. */
export interface FoodsListState {
  /** a search is running, so the page shows a spinner and says nothing else */
  searching: boolean;
  /** the search box has something in it */
  hasSearchText: boolean;
  /** how many foods are on screen */
  resultCount: number;
  /** the search pass threw, so there is no list to show (#4100) */
  searchFailed: boolean;
  /** how the server-side search ended (#3743, #5114) */
  serverSearch: ServerSearchOutcome;
  /**
   * our best guess that the device has no working connection — the page reads it as
   * `!OnlineStateService.isLikelyOnline()`.
   *
   * It is a GUESS, and only ever used to pick the wording and colour of a passive card. It is
   * true either when the browser reports itself offline or when our own last probe of the
   * server did not answer, and that probe state can sit stale for minutes on a device that is
   * working perfectly well — so it can read true on a device that is fine. That is affordable
   * here and nowhere else: `online-state.service.ts` says a passive indicator "is allowed to
   * be briefly wrong", and nothing here refuses the user anything on the strength of it.
   */
  deviceLikelyOffline: boolean;
  /** the search text looked like a barcode rather than a name */
  searchIsBarcode: boolean;
  /** the label scanner is available to this user */
  labelScanEnabled: boolean;
  /** at least one list filter is on, so it may be what hides every food */
  filtersActive: boolean;
}

/**
 * Pick the card that goes ABOVE the list, which explains why the list holds nothing but foods
 * already stored on this device (#5114).
 *
 * It appears only when there is actually a list to explain. With no results at all, the bottom
 * message already says the server could not be reached, and saying it twice on one empty screen
 * is worse than saying it once.
 */
export function pickFoodsSearchCard(state: FoodsListState): FoodsSearchCard {
  // while a search is running the spinner is the whole story, exactly as for the message below
  if (state.searching) {
    return FoodsSearchCard.None;
  }

  // the server search answered, so nothing needs explaining. This is also the answer when it
  // was never asked at all — see ServerSearchOutcome.Contributed
  if (state.serverSearch === ServerSearchOutcome.Contributed) {
    return FoodsSearchCard.None;
  }

  // no list to explain. The whole pass failing (#4100) and finding nothing both end with a
  // message BELOW the list that already says the server could not be reached, with its own
  // retry button
  if (state.searchFailed || state.resultCount === 0) {
    return FoodsSearchCard.None;
  }

  // a device we believe is offline gets gentler wording and a calmer colour: being offline is
  // an ordinary thing that happens, not a fault. See FoodsListState.deviceLikelyOffline for why
  // this is a guess rather than a fact, and why a wrong guess here is affordable
  if (state.searchIsBarcode) {
    // a barcode search is never given the staples, so its cards must not mention them
    return state.deviceLikelyOffline
      ? FoodsSearchCard.OfflineShowingLocalFoodsBarcode
      : FoodsSearchCard.ServerProblemShowingLocalFoodsBarcode;
  }
  return state.deviceLikelyOffline
    ? FoodsSearchCard.OfflineShowingLocalFoods
    : FoodsSearchCard.ServerProblemShowingLocalFoods;
}

/**
 * Pick the one message that closes the list. The order of the checks IS the precedence, and it
 * matches what the hand-written conditions did before this existed, with one deliberate
 * decision added:
 *
 * `searchFailed` wins over everything, including a non-empty result list. The old template let
 * the "couldn't load foods" block show without asking whether results were on screen, so if
 * both had ever been true it would have rendered that message AND a closing note underneath.
 * That combination cannot happen — `searchFailed` is set in exactly one place, the catch in
 * `FoodsPage.searchFoodsAsync_internal`, which clears the list on the very next line, and it is
 * cleared again at the start of every search — but the answer is better written down than left
 * to the order two `@if` blocks happen to appear in.
 */
export function pickFoodsListMessage(state: FoodsListState): FoodsListMessage {
  // if a search is running, the spinner is the whole story
  if (state.searching) {
    return FoodsListMessage.None;
  }

  // if the search could not be done at all, say so whatever else is true
  if (state.searchFailed) {
    return FoodsListMessage.SearchFailed;
  }

  // if nothing has been searched for, point at the search box — but only when there is no list
  // of previously logged foods already on screen
  if (!state.hasSearchText) {
    if (state.resultCount > 0) {
      return FoodsListMessage.None;
    }
    // if a filter is on, it may be what emptied the list, so say that too
    return state.filtersActive ? FoodsListMessage.NothingMatchesFilters : FoodsListMessage.UseSearchBar;
  }

  // for a search that found nothing, a server search that never answered is a different answer
  // from "no such food" — never let a network blip or a timeout read as "this food does not
  // exist" (#3743, #5114)
  if (state.resultCount === 0) {
    if (state.serverSearch !== ServerSearchOutcome.Contributed) {
      return FoodsListMessage.NoResultsConnectionError;
    }
    // a barcode search sets the filters aside, so only a name search can have been emptied by one
    if (state.searchIsBarcode) {
      return FoodsListMessage.NoResultsForBarcode;
    }
    return state.filtersActive ? FoodsListMessage.NoResultsForTextWithFilters : FoodsListMessage.NoResultsForText;
  }

  // results are on screen. A server search that did not answer is explained by the card ABOVE
  // the list (pickFoodsSearchCard), so the bottom of the list keeps its ordinary note — which
  // still carries the "create a food or recipe" buttons the user needs when nothing fits.
  if (state.searchIsBarcode && state.labelScanEnabled) {
    return FoodsListMessage.ResultsOfferScanLabel;
  }
  return FoodsListMessage.ResultsNotWhatYouWanted;
}
